"use client"

import { Box, Flex, Text, Collapse, Icon } from "@chakra-ui/react"
import { ChevronDownIcon } from "@chakra-ui/icons"
import { neutral, brand, type, radius, motion, elevation } from "../../theme/tokens"

/**
 * A section of a report.
 *
 * Replaces the previous AccordionSection, which gave every section a
 * full-width saturated teal gradient bar with white text. Five of those
 * stacked down a page meant the loudest thing on a screen full of numbers was
 * the furniture. Here the header is typographic — an uppercase eyebrow on a
 * hairline rule — and the panel below carries the only fill.
 *
 * The old version also faded each section in with an IntersectionObserver at
 * `opacity: 0`. If you scrolled past one before the observer fired, or jumped
 * with the scrollbar, that section stayed permanently invisible while still
 * occupying its full height. There is no reveal animation here: report content
 * is present when the page is.
 */
const Section = ({
  title,
  description,
  meta,
  icon,
  // Each section carries its own accent. An earlier pass stripped the icons
  // and colour coding as decoration — they weren't: on a page of five stacked
  // reports they are how you find the one you want without reading.
  accent = brand[500],
  isOpen = true,
  onToggle,
  children,
  ...rest
}) => {
  const collapsible = typeof onToggle === "function"

  return (
    <Box as="section" mb={6} {...rest}>
      <Flex
        align="center"
        gap={3}
        mb={2.5}
        pb={2}
        borderBottom="1px solid"
        borderColor={neutral[200]}
        role={collapsible ? "button" : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? isOpen : undefined}
        cursor={collapsible ? "pointer" : "default"}
        onClick={collapsible ? onToggle : undefined}
        onKeyDown={
          collapsible
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  onToggle()
                }
              }
            : undefined
        }
        _focusVisible={{ outline: "2px solid", outlineColor: "primary1", outlineOffset: "2px" }}
      >
        {collapsible && (
          <Icon
            as={ChevronDownIcon}
            boxSize="16px"
            color={neutral[400]}
            transform={isOpen ? "rotate(0deg)" : "rotate(-90deg)"}
            transition={`transform ${motion.base}`}
            flexShrink={0}
          />
        )}

        {icon && (
          <Flex
            align="center"
            justify="center"
            boxSize="30px"
            flexShrink={0}
            borderRadius={radius.sm}
            bg={accent}
            color={neutral[0]}
            boxShadow={elevation.raised}
          >
            <Icon as={icon} boxSize="14px" />
          </Flex>
        )}

        <Box minW={0}>
          <Text
            as="h2"
            fontSize={type.size.micro}
            fontWeight={type.weight.bold}
            letterSpacing={type.tracking.label}
            textTransform="uppercase"
            color={accent}
          >
            {title}
          </Text>
          {description && (
            <Text fontSize={type.size.caption} color={neutral[500]} mt="2px">
              {description}
            </Text>
          )}
        </Box>

        <Box flex="1" />
        {meta}
      </Flex>

      {collapsible ? (
        // Not `unmountOnExit`: these panels own their own fetches, so
        // unmounting made every collapse-and-reopen re-hit the API and blank
        // the table while it reloaded. Matches the original behaviour.
        <Collapse in={isOpen} animateOpacity={false}>
          {children}
        </Collapse>
      ) : (
        children
      )}
    </Box>
  )
}

/** The white card that report content sits on. */
export const Panel = ({ children, padded = true, ...rest }) => (
  <Box
    bg={neutral[0]}
    border="1px solid"
    borderColor={neutral[200]}
    borderRadius={radius.md}
    boxShadow={elevation.raised}
    p={padded ? 4 : 0}
    minW={0}
    overflow="hidden"
    {...rest}
  >
    {children}
  </Box>
)

/** Panel with a small caption above its content — used for chart tiles. */
export const ChartPanel = ({ title, hint, height = "310px", children, ...rest }) => (
  <Panel display="flex" flexDirection="column" {...rest}>
    <Flex align="baseline" justify="space-between" gap={2} mb={2}>
      <Text
        fontSize={type.size.micro}
        fontWeight={type.weight.semibold}
        letterSpacing={type.tracking.label}
        textTransform="uppercase"
        color={neutral[500]}
      >
        {title}
      </Text>
      {hint && (
        <Text fontSize={type.size.caption} color={neutral[400]}>
          {hint}
        </Text>
      )}
    </Flex>
    <Box flex="1" minH={height} position="relative">
      {children}
    </Box>
  </Panel>
)

export default Section
